// One owner for current-version saved-copy actions in this tab and origin.
// Contexts are capabilities, not serializable ownership claims. Helpers must
// await their nested work and pass the existing context rather than reacquire.
const activeOrders = new Set();
const contexts = new WeakMap();

const unavailable = () => new Error('Safe saved-copy changes are unavailable in this browser. Use an updated browser on this device; do not clear site data.');
const invalidContext = () => new Error('This saved-copy operation is no longer active or belongs to another order. Nothing was changed.');

export async function withDeliveryCopyOperation(orderId, action, { requireCrossContext = false, context } = {}) {
  if (typeof orderId !== 'string' || !orderId.length) throw new Error('This saved copy has no usable local key. Nothing was changed.');
  if (typeof action !== 'function') throw new Error('A saved-copy operation is required.');

  if (context !== undefined) {
    const owner = contexts.get(context);
    if (!owner?.active || owner.orderId !== orderId) throw invalidContext();
    if (requireCrossContext && !owner.crossContext) throw unavailable();
    return action(context);
  }

  const run = async (crossContext) => {
    if (activeOrders.has(orderId)) throw new Error('This saved copy is busy. Wait for saving or resending to finish.');
    const ownedContext = Object.freeze({});
    const owner = { active: true, orderId, crossContext };
    contexts.set(ownedContext, owner);
    activeOrders.add(orderId);
    try { return await action(ownedContext); }
    finally { owner.active = false; activeOrders.delete(orderId); }
  };

  const locks = globalThis.navigator?.locks;
  if (typeof locks?.request === 'function') {
    return locks.request(`lvb-delivery-copy:${orderId}`, { ifAvailable: true }, (lock) => {
      if (!lock) throw new Error('This saved copy is busy in another operation. Wait for it to finish.');
      return run(true);
    });
  }
  if (requireCrossContext) throw unavailable();
  return run(false);
}
